import { useMemo } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import {
  ChevronDown,
  RotateCcw,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { cn } from "../../lib/utils";
import {
  DONATION_FILTER_ALL,
  countActiveDonationFilters,
  donationListFilterInitialState,
  type DonationListFilterState,
  type DonationSchemeFilterOption,
  type DonationTypeFilterOption,
} from "./donationListFilterUtils";

const STATUS_OPTIONS = [
  { value: DONATION_FILTER_ALL, label: "All status" },
  { value: "success", label: "Success" },
  { value: "created", label: "Created" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "captured", label: "Captured" },
] as const;

type DonationListFiltersProps = {
  filters: DonationListFilterState;
  onFiltersChange: (filters: DonationListFilterState) => void;
  filtersExpanded: boolean;
  onFiltersExpandedChange: (open: boolean) => void;
  donationTypes: DonationTypeFilterOption[];
  paymentModes: DonationTypeFilterOption[];
  schemes: DonationSchemeFilterOption[];
};

export function DonationListFilters({
  filters,
  onFiltersChange,
  filtersExpanded,
  onFiltersExpandedChange,
  donationTypes,
  paymentModes,
  schemes,
}: DonationListFiltersProps) {
  const panelFilterCount = useMemo(
    () => countActiveDonationFilters(filters),
    [filters]
  );

  const patch = (partial: Partial<DonationListFilterState>) => {
    onFiltersChange({ ...filters, ...partial });
  };

  const handleReset = () => {
    onFiltersChange(donationListFilterInitialState);
  };

  const hasActiveFilters =
    panelFilterCount > 0 || filters.search.trim().length > 0;

  return (
    <Collapsible open={filtersExpanded} onOpenChange={onFiltersExpandedChange}>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="donation-list-search"
            placeholder="Search name, email, phone, campaign..."
            value={filters.search}
            onChange={e => patch({ search: e.target.value })}
            className="h-10 border-input bg-background pl-10 shadow-sm"
            aria-label="Search donations"
          />
        </div>

        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-10 shrink-0 gap-2 border-zinc-200 bg-white px-4 font-semibold shadow-sm hover:bg-zinc-50 hover:text-[#8B3232]"
            aria-expanded={filtersExpanded}
            aria-controls="donation-list-filters-panel"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#FCECEC]">
              <SlidersHorizontal className="h-4 w-4 text-[#8B3232]" />
            </span>
            Filters
            {panelFilterCount > 0 && (
              <Badge className="h-5 min-w-5 border-0 bg-[#8B3232] px-1.5 text-[11px] text-white hover:bg-[#8B3232]">
                {panelFilterCount}
              </Badge>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                filtersExpanded && "rotate-180"
              )}
            />
          </Button>
        </CollapsibleTrigger>

        {hasActiveFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-10 shrink-0 gap-2 text-muted-foreground"
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        )}
      </div>

      <CollapsibleContent
        id="donation-list-filters-panel"
        className="data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 overflow-visible"
      >
        <Card className="mb-4 overflow-visible border-zinc-200/90 shadow-sm">
          <CardHeader className="space-y-1 pb-2">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#FCECEC]">
                <SlidersHorizontal className="h-4 w-4 text-[#8B3232]" />
              </span>
              Filter donations
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-visible pt-0">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <div className="min-w-0 space-y-1.5">
                <Label
                  htmlFor="donation-filter-message"
                  className="text-xs font-medium text-muted-foreground"
                >
                  Donor message
                </Label>
                <Input
                  id="donation-filter-message"
                  placeholder="Filter by donor message..."
                  value={filters.message}
                  onChange={e => patch({ message: e.target.value })}
                  className="h-10 border-input bg-background shadow-sm"
                />
              </div>
              <FilterSelect
                id="donation-filter-type"
                label="Donation type"
                value={filters.donation_type}
                onValueChange={v => patch({ donation_type: v })}
                options={[
                  { value: DONATION_FILTER_ALL, label: "All types" },
                  ...donationTypes,
                ]}
              />
              <FilterSelect
                id="donation-filter-status"
                label="Payment status"
                value={filters.status}
                onValueChange={v => patch({ status: v })}
                options={STATUS_OPTIONS.map(o => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
              <FilterSelect
                id="donation-filter-payment-mode"
                label="Payment mode"
                value={filters.payment_mode}
                onValueChange={v => patch({ payment_mode: v })}
                options={[
                  { value: DONATION_FILTER_ALL, label: "All modes" },
                  ...paymentModes,
                ]}
              />
              <FilterSelect
                id="donation-filter-scheme"
                label="Campaign (scheme)"
                value={filters.scheme}
                onValueChange={v => patch({ scheme: v })}
                options={[
                  { value: DONATION_FILTER_ALL, label: "All campaigns" },
                  ...schemes,
                ]}
              />
            </div>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
}

function FilterSelect({
  id,
  label,
  value,
  onValueChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          id={id}
          className="h-10 w-full border-input bg-background shadow-sm"
        >
          <SelectValue placeholder={`All ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map(opt => (
            <SelectItem key={`${id}-${opt.value}`} value={opt.value}>
              <span className="block max-w-[240px] truncate">{opt.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
